/** @jsx jsx */
import { useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import AkScreenIcon from '@atlaskit/icon/core/screen';
import { token } from '@atlaskit/tokens';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { SummaryItem } from '@atlassian/jira-development-summary-common/src/ui/summary-item/index.tsx';
import {
	fireUIAnalytics,
	FireScreenAnalytics,
	ContextualAnalyticsData,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const verticalAlignStyle = css({
	verticalAlign: 'middle',
});

const ScreenIcon = (
	<span css={verticalAlignStyle} aria-hidden>
		<AkScreenIcon color={token('color.link')} label="" />
	</span>
);

const buildVsCodeUrl = (issueKey: string, host: string): string => {
	const issueKeyQs = encodeURIComponent(issueKey);
	const hostQs = encodeURIComponent(host);
	return `vscode://atlassian.atlascode/openJiraIssue?key=${issueKeyQs}&site=${hostQs}&source=jira`;
};

const OpenInVSCodeItem = ({ issueKey }: Props) => {
	const { formatMessage } = useIntl();

	const title = formatMessage(messages.openWithMessage);
	const clickUrl = useMemo(
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		() => buildVsCodeUrl(issueKey, document.location.hostname),
		[issueKey],
	);

	return (
		<ContextualAnalyticsData sourceName="openInVSCodeButton" sourceType={SCREEN}>
			<FireScreenAnalytics />
			<SummaryItem
				title={title}
				issueKey={issueKey}
				icon={ScreenIcon}
				linkFormatting
				href={clickUrl}
				onClick={(_, analyticEvent) => {
					return fireUIAnalytics(analyticEvent, 'openInVSCodeButton');
				}}
				data-testid="development-summary-development-field.ui.summary.open-in-vscode.open-in-vscode-summary-item"
			/>
		</ContextualAnalyticsData>
	);
};

export default OpenInVSCodeItem;
